import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { ApiResponse } from 'src/app/interfaces/api-response';
import { AccountSettings } from 'src/app/interfaces/account-settings';
import { TokenContainer } from 'src/app/interfaces/tokencontainer';
import { CompanyGroup } from 'src/app/interfaces/company-group';
import { AccountService } from 'src/app/services/account.service';
import { AlertService } from 'src/app/services/alert.service';
import { ApplicationService } from 'src/app/services/application.service';
import { AuthenticateService } from 'src/app/services/authenticate.service';
import { HelperService } from 'src/app/services/helper.service';
import { TranslateService } from '@ngx-translate/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SelectLinkedDealerComponent } from 'src/app/components/login-components/select-linked-dealer/select-linked-dealer.component';

@Component({
  selector: 'app-authenticated-page',
  templateUrl: './authenticated-page.component.html',
  styleUrls: ['./authenticated-page.component.scss'],
  standalone: false,
})
export class AuthenticatedPageComponent implements OnInit {
  loading = false;
  hasError = false;
  redirectUrl = '';
  companyGroups: CompanyGroup[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authenticateService: AuthenticateService,
    private accountService: AccountService,
    private applicationService: ApplicationService,
    private alertService: AlertService,
    private translateService: TranslateService,
    private helperService: HelperService,
    private modalService: NgbModal,
  ) {}

  ngOnInit(): void {
    const key = this.route.snapshot.paramMap.get('key') ?? '';

    this.redirectUrl =
      this.route.snapshot.queryParamMap.get('redirectUrl') ?? '';

    this.loading = true;

    this.authenticateService.authConnect(key).subscribe({
      next: (data: ApiResponse) => {
        if (data.success) {
          const tokens: TokenContainer = data.result;

          if (!tokens.linked && typeof tokens.okta_registration === 'undefined') {
            this.handleLogin(tokens);
          } else if (tokens.session_token && tokens.okta_registration) {
            const url = tokens.session_token.replace(/^.*\/\/[^\/]+/, '');
            this.router.navigateByUrl(url);
          } else {
            this.authenticateService.saveTokens(tokens);

            this.accountService
              .getLinkedDealers()
              .subscribe((apiResponse: ApiResponse) => {
                if (this.helperService.checkResponse(apiResponse)) {
                  const modalRef = this.modalService.open(
                    SelectLinkedDealerComponent,
                    { centered: true, container: '#modalContainer' }
                  );
                  modalRef.componentInstance.dealers = apiResponse.result;
                  modalRef.componentInstance.dealerSelected.subscribe(
                    (tokenContainer: TokenContainer) => {
                      this.handleLogin(tokenContainer);
                      modalRef.close();
                    }
                  );
                }
              });
          }
        } else {
          this.hasError = true;
          this.loading = false;
          this.alertService.showErrorNotification(
            data.result
              ? data.result
              : this.translateService.instant('INCORRECT_CREDENTIALS')
          );
        }
      },
      error: () => {
        this.hasError = true;
        this.loading = false;
      },
    });
  }

  private handleLogin(tokens: TokenContainer): void {
    this.authenticateService.validLogin();
    this.authenticateService.saveTokens(tokens);

    this.accountService.getAccountSettings().subscribe((response: any) => {
      if (response && response.result) {
        const accountSettings: AccountSettings = response.result;

        if (this.accountService.isHelpdesk()) {
          localStorage.setItem('defaultCompanyGroup', accountSettings.company_group);
        }

        this.accountService.saveAccountSettings(accountSettings);
        this.loading = true;

        if (
          (accountSettings.okta && accountSettings.registered) ||
          (!accountSettings.okta && accountSettings.verified)
        ) {
          forkJoin([
            this.accountService.getAvailableCompanyGroups()
          ]).subscribe((apiResponse: any[]) => {
            if (apiResponse[0].success) {
              this.companyGroups = apiResponse[0].result;
              this.applicationService.saveCompanyGroups(apiResponse[0].result);
              sessionStorage.setItem('newSettings', '1');

              if (typeof accountSettings.company_group === 'undefined') {
                const groups = this.applicationService.getAvailableCompanyGroups();
                accountSettings.company_group = groups[0];
              }

              const returnUrl = this.redirectUrl
                ? decodeURIComponent(this.redirectUrl)
                : decodeURIComponent(this.getReturnUrl(accountSettings.company_group));

              if (tokens.session_token) {
                  const hasSalesForceRedirect =
                    this.helperService.hasSalesForceRedirect(this.redirectUrl);

                  let oktaReturnUrl = '';
                  if (this.redirectUrl) {
                    if (this.redirectUrl.includes(window.location.origin)) {
                      oktaReturnUrl = this.redirectUrl;
                    } else if (
                      !this.redirectUrl.includes('https://') &&
                      !hasSalesForceRedirect
                    ) {
                      oktaReturnUrl = encodeURIComponent(
                        `${window.location.origin}/${this.redirectUrl}`
                      );
                    } else {
                      if (hasSalesForceRedirect) {
                        this.accountService
                          .portalSignin()
                          .subscribe((apiResponse: ApiResponse) => {
                            this.loading = false;
                            if (this.helperService.checkResponse(apiResponse)) {
                              oktaReturnUrl = encodeURIComponent(apiResponse.result);
                              this.authenticateService.redirectToOkta(tokens, oktaReturnUrl);
                            } else {
                              this.alertService.showPopup(
                                apiResponse
                                  ? apiResponse.result
                                  : this.translateService.instant('ERROR'),
                                'error',
                                0
                              );
                            }
                          });
                      } else {
                        oktaReturnUrl = encodeURIComponent(
                          this.helperService.externalRedirect(this.redirectUrl)
                        );
                      }
                    }
                  } else {
                    oktaReturnUrl = encodeURIComponent(
                      `${window.location.origin}/${returnUrl}`
                    );
                  }

                  if (!hasSalesForceRedirect) {
                    this.authenticateService.redirectToOkta(tokens, oktaReturnUrl);
                  }
              } else {
                this.router.navigateByUrl(returnUrl);
              }
            } else {
              this.alertService.showErrorNotification(
                this.translateService.instant('ERROR_500')
              );
              this.loading = false;
            }
          });
        } else {
          sessionStorage.setItem('unverified', '1');
          if (accountSettings.okta && !accountSettings.registered) {
            this.router.navigateByUrl('/register');
          }
          if (!accountSettings.okta && !accountSettings.verified) {
            this.router.navigateByUrl('/verify');
          }
        }
      }
    });
  }

  private getReturnUrl(companyGroup: string): string {
    if (this.applicationService.isPartnerPortal()) {
      return `${companyGroup}/companyPage/partnerPortal`;
    }
    const returningVisitor = localStorage.getItem('returning');
    const url =
      this.route.snapshot.queryParams['returnUrl'] ||
      (!returningVisitor &&
        !this.accountService.isHelpdesk() &&
        this.companyGroups.length > 1)
        ? 'start'
        : `${companyGroup}/home`;
    localStorage.setItem('returning', '1');
    return url;
  }
}
